import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Pagination,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import PageHeader from './PageHeader';
import { getIntrusionAccounts, createAccount, updateIntrusionAccount } from '../utils/api';

const IntrusionAccounts = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    address: '',
    description: '',
    company_phone_number: '',
  });
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    offset: 0,
    has_next: false,
    has_prev: false,
  });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadCompanies();
  }, [currentPage]);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const offset = (currentPage - 1) * pagination.limit;
      const response = await getIntrusionAccounts(pagination.limit, offset, true);
      setCompanies(response.companies || []);
      setPagination({
        total: response.total || 0,
        limit: response.limit || 10,
        offset: response.offset || 0,
        has_next: response.has_next || false,
        has_prev: response.has_prev || false,
      });
    } catch (error) {
      console.error('Error loading intrusion accounts:', error);
      showSnackbar('Failed to load intrusion accounts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (company = null) => {
    if (company) {
      setEditingCompany(company);
      setFormData({
        company_name: company.company_name || '',
        address: company.address || '',
        description: company.description || '',
        company_phone_number: company.company_phone_number || '',
      });
    } else {
      setEditingCompany(null);
      setFormData({
        company_name: '',
        address: '',
        description: '',
        company_phone_number: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCompany(null);
    setFormData({
      company_name: '',
      address: '',
      description: '',
      company_phone_number: '',
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingCompany) {
        // Update existing intrusion account
        const payload = {
          ...formData,
        };
        await updateIntrusionAccount(editingCompany.company_id, payload);
        showSnackbar('Intrusion account updated successfully', 'success');
      } else {
        // Create new intrusion account - only send company_name
        await createAccount(formData.company_name);
        showSnackbar('Intrusion account created successfully', 'success');
      }
      
      handleCloseDialog();
      loadCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
      showSnackbar(error.message || 'Failed to save company', 'error');
    }
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <Container maxWidth="xl" sx={{ py: 0 }}>
      <PageHeader
        title="Intrusion Accounts"
        subtitle="Manage intrusion accounts - create accounts with minimal information"
      />

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 3,
            }}
          >
            <Typography variant="h6">Intrusion Accounts</Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadCompanies}
                disabled={loading}
                fullWidth={isMobile}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                sx={{ backgroundColor: primary, '&:hover': { backgroundColor: '#2d3a5f' } }}
                fullWidth={isMobile}
              >
                Create Account
              </Button>
            </Stack>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            isMobile ? (
              companies.length === 0 ? (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No intrusion accounts found
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {companies.map((company) => (
                    <Card key={company.company_id} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                              {company.company_name}
                            </Typography>
                            {company.description ? (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                {company.description}
                              </Typography>
                            ) : null}
                            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
                              <Typography variant="caption" color="text.secondary">
                                Created: {company.created_at ? new Date(company.created_at).toLocaleDateString() : '-'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Updated by: {company.updated_by || '-'}
                              </Typography>
                            </Stack>
                          </Box>
                          <Tooltip title="Edit Account">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(company)}
                              color="primary"
                              sx={{ flexShrink: 0 }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <TableContainer component={Paper} variant="outlined">
                  <Table sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Account Name</TableCell>
                        <TableCell>Created At</TableCell>
                        <TableCell>Last Updated By</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {companies.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">No intrusion accounts found</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        companies.map((company) => (
                          <TableRow key={company.company_id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {company.company_name}
                              </Typography>
                              {company.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {company.description.length > 50
                                    ? `${company.description.substring(0, 50)}...`
                                    : company.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {company.created_at
                                ? new Date(company.created_at).toLocaleDateString()
                                : '-'}
                            </TableCell>
                            <TableCell>
                              {company.updated_by || '-'}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Edit Account">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDialog(company)}
                                  color="primary"
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCompany ? 'Edit Intrusion Account' : 'Create New Intrusion Account'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Account Name"
              name="company_name"
              value={formData.company_name}
              onChange={handleInputChange}
              required
              fullWidth
              helperText={editingCompany ? null : "Only account name is required."}
            />
            {editingCompany && (
              <>
                <TextField
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  fullWidth
                />
                <TextField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  fullWidth
                />
                <TextField
                  label="Phone Number"
                  name="company_phone_number"
                  value={formData.company_phone_number}
                  onChange={handleInputChange}
                  fullWidth
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingCompany ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

const primary = '#40518A';

export default IntrusionAccounts;
